import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canWriteCategory } from "@/lib/roles";

const ProjectContext = createContext(null);

function friendlyLoadError(err) {
  const status = err?.response?.status;
  if (status === 404) {
    return { status, message: "This project could not be found. It may have been deleted." };
  }
  if (status === 403) {
    return { status, message: "You do not have access to this project." };
  }
  return { status: status || null, message: "Couldn't load this project. Please try again." };
}

export function ProjectProvider({ children }) {
  const { projectId } = useParams();
  const { user, seesWholePortfolio } = useAuth();
  const [project, setProject] = useState(null);
  const [projectRole, setProjectRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const refresh = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [{ data: proj }, { data: members }] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/members`),
      ]);
      setProject(proj);
      if (seesWholePortfolio) {
        setProjectRole("PROJECT_MANAGER");
      } else {
        const mine = members.find((m) => m.user_id === user?.id);
        setProjectRole(mine?.role || null);
      }
    } catch (err) {
      setProject(null);
      setProjectRole(null);
      setLoadError(friendlyLoadError(err));
    } finally {
      setLoading(false);
    }
  }, [projectId, user, seesWholePortfolio]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isProjectManager = projectRole === "PROJECT_MANAGER";

  return (
    <ProjectContext.Provider
      value={{
        projectId,
        project,
        projectRole,
        isProjectManager,
        canWrite: (category) => canWriteCategory(projectRole, category),
        loading,
        loadError,
        refresh,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export const useProjectCtx = () => useContext(ProjectContext);
